import { env } from '@/config/env';
import type { ProductPlan } from '@/features/products/types';
import { formatMoney } from '@/shared/lib/money';
import { isTrialPlan, renewalPhrase, trialDurationPhrase } from './pricing';

/**
 * First-person opt-in consent ("I accept that …"), used on the single-plan
 * checkout. Adapts to trial / recurring / one-time without hardcoded prices.
 */
export function buildConsentDisclaimer(plan: ProductPlan): string {
  const brand = env.VITE_APP_NAME;
  const today = formatMoney(plan.price, plan.currency);
  const phrase = renewalPhrase(plan);

  if (isTrialPlan(plan)) {
    const recurring = formatMoney(plan.recurringPrice ?? plan.price, plan.currency);
    const trialPhrase = trialDurationPhrase(plan);
    return `Your ${trialPhrase}trial will cost only ${today}. Without cancellation, before the selected discounted intro plan ends, I accept that ${brand} will automatically charge ${recurring}${phrase ? ` ${phrase}` : ''} until I cancel.`;
  }

  if (phrase) {
    return `You'll be charged ${today} today. Without cancellation, I accept that ${brand} will automatically charge ${today} ${phrase} until I cancel.`;
  }

  return `You'll be charged ${today} today. This is a one-time payment — no recurring charges.`;
}

export function buildRenewalDisclaimer(plan: ProductPlan): string {
  const phrase = renewalPhrase(plan);
  const price = formatMoney(plan.price, plan.currency);

  if (isTrialPlan(plan)) {
    const recurring = formatMoney(plan.recurringPrice ?? plan.price, plan.currency);
    const renews = `${recurring}${phrase ? ` ${phrase}` : ''}`;
    const trialPhrase = trialDurationPhrase(plan);
    return `Your ${trialPhrase}trial starts today. You can cancel anytime before it ends and won't be charged the full price. After the trial, your subscription renews automatically at ${renews} unless canceled. No refunds for unused time after renewal.`;
  }

  if (!phrase) {
    return `You'll be charged ${price} today. This is a one-time payment — no recurring charges.`;
  }

  return `You'll be charged ${price} today. Your subscription then renews automatically at ${price} ${phrase} unless canceled. You can cancel anytime. No refunds for unused time after renewal.`;
}
